<template>
  <!--
    MK3 app shell — replaces the App.vue chrome (CoreUI navbar +
    Sidebar.vue + breadcrumb + footer) when `?ui=mk3` is in the URL or
    the flag has been stickied in localStorage.

    Phase 1a scope:
      - Render new shell (Sidebar + TopBar + light content area)
      - Dashboards inside `<router-view>` keep their existing styling.
        They will look slightly off (Bootstrap/CoreUI dashboard chrome
        inside a Tailwind shell) — that's expected. Phase 1b re-skins
        Summary as the reference. Phase 1c ports the filter chip bar.

    Notifications stay global (mounted from the legacy shell) so
    components calling `$notify(...)` work identically under either
    shell. The MK3 shell does NOT mount its own <notifications/> — see
    AppShellSwitcher in App.vue for the placement.
  -->
  <!--
    `mk3` class is the scoping hook for MK3-only global CSS rules
    (see src/assets/tailwind.css @layer base). Adding rules under
    `.mk3 selector { ... }` lets us set MK3-only defaults — like
    paragraph spacing — without touching the legacy MK2 shell.
  -->
  <div class="mk3 tw-flex tw-h-screen tw-bg-gray-100">
    <Mk3Sidebar />

    <div class="tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
      <Mk3TopBar />

      <main class="tw-flex-1 tw-overflow-x-hidden tw-overflow-y-auto tw-bg-gray-100 tw-p-6">
        <PrintLayout>
          <router-view />
        </PrintLayout>

        <!--
          Sitewide footer (MK3). Lives INSIDE `<main>` after the
          router-view so it scrolls with page content and only
          surfaces when the user reaches the bottom — not pinned to
          the viewport. The negative horizontal margin / padding
          (-mx-6 / mt-6) cancels main's px-6 so the footer's white
          band stretches edge-to-edge across the content area.
        -->
        <footer class="tw--mx-6 tw-mt-6 tw-bg-white tw-border-t tw-border-gray-200 tw-py-3 tw-text-center tw-text-sm tw-text-gray-500">
          <div>Powered by Travel Analytics Reporting</div>
          <div>Copyright &copy; 2026</div>
        </footer>
      </main>
    </div>

    <!-- Mobile overlay — closes sidebar when tapped -->
    <div
      v-if="!uiStore.isDesktop && !uiStore.isSidebarCollapsed"
      class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-30 lg:tw-hidden"
      @click="uiStore.toggleSidebar"
    />
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue'

  import Mk3Sidebar from './Sidebar.vue'
  import Mk3TopBar from './TopBar.vue'
  import PrintLayout from '@/views/PrintLayout.vue'
  import { useUiStore } from '@/stores/ui'

  defineOptions({ name: 'Mk3AppShell' })

  const uiStore = useUiStore()

  function handleResize () { uiStore.checkScreenSize() }

  onMounted(() => {
    uiStore.checkScreenSize()
    window.addEventListener('resize', handleResize)
  })
  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })
</script>
